import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BookOpen, Star, Twitter, Instagram, Youtube, Globe, Coins, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import ShareButton from "@/components/ShareButton";

/**
 * Public author microsite. Route: /author/:slug. Shows bio, socials, all
 * published books, aggregated star ratings + review count as social proof.
 */
export default function AuthorPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/authors/${slug}`); setData(r.data); }
      catch (e) { setErr(e?.response?.data?.detail || "Author not found"); }
    })();
  }, [slug]);

  if (err) return <main className="max-w-2xl mx-auto px-5 py-24 text-slate-400" data-testid="author-page-error">{err}</main>;
  if (!data) return <main className="max-w-2xl mx-auto px-5 py-24 text-slate-500 fl-mono text-xs uppercase tracking-widest">// Loading…</main>;

  const { profile, books, stats } = data;
  const socials = [
    profile.twitter && { icon: Twitter, label: "Twitter", href: profile.twitter.startsWith("http") ? profile.twitter : `https://twitter.com/${profile.twitter.replace(/^@/, "")}` },
    profile.instagram && { icon: Instagram, label: "Instagram", href: profile.instagram.startsWith("http") ? profile.instagram : `https://instagram.com/${profile.instagram.replace(/^@/, "")}` },
    profile.youtube && { icon: Youtube, label: "YouTube", href: profile.youtube.startsWith("http") ? profile.youtube : `https://youtube.com/${profile.youtube}` },
    profile.website && { icon: Globe, label: "Website", href: profile.website },
  ].filter(Boolean);

  return (
    <main className="max-w-5xl mx-auto px-5 py-12" data-testid="author-page">
      <Link to="/flip" className="inline-flex items-center gap-2 fl-mono text-[11px] tracking-widest uppercase text-slate-400 hover:text-violet-400 mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Fliboe Flip
      </Link>

      {/* Hero */}
      <div className="fl-card p-6 md:p-8 bg-gradient-to-br from-violet-500/10 via-black/40 to-fuchsia-500/10 border-violet-400/20">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          {profile.avatar_url ? (
            <img src={profile.avatar_url} alt="" className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border border-white/10" data-testid="author-avatar" />
          ) : (
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-violet-500/15 border border-violet-400/30 flex items-center justify-center shrink-0">
              <BookOpen className="w-10 h-10 text-violet-300" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-violet-400">// Fliboe Flip author</div>
            <h1 className="fl-display text-3xl md:text-5xl font-black text-white mt-1 tracking-tighter" data-testid="author-name">{profile.display_name}</h1>
            {profile.bio && <p className="text-slate-300 mt-3 leading-relaxed max-w-2xl">{profile.bio}</p>}
            {/* Stats row */}
            <div className="mt-5 flex flex-wrap gap-3 items-center" data-testid="author-stats">
              <span className="fl-pill">{stats.book_count} book{stats.book_count === 1 ? "" : "s"}</span>
              <span className="fl-pill">{stats.chapter_count} chapters</span>
              {stats.review_count > 0 && (
                <span className="fl-pill flex items-center gap-1">
                  <Star className="w-3 h-3 fill-emerald-400 text-emerald-400" /> {stats.avg_stars} · {stats.review_count} review{stats.review_count === 1 ? "" : "s"}
                </span>
              )}
              {profile.tip_jar_url && (
                <a
                  href={profile.tip_jar_url.startsWith("http") ? profile.tip_jar_url : `https://${profile.tip_jar_url}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="fl-btn ghost !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
                  data-testid="author-tip"
                >
                  <Coins className="w-3.5 h-3.5" /> Tip the author
                </a>
              )}
            </div>
            {socials.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-3" data-testid="author-socials">
                {socials.map(s => (
                  <a key={s.label} href={s.href} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-violet-300 inline-flex items-center gap-1.5 text-sm">
                    <s.icon className="w-4 h-4" /> {s.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Books */}
      <section className="mt-10">
        <h2 className="fl-display text-2xl font-bold text-white mb-4">Books</h2>
        {books.length === 0 ? (
          <div className="fl-card p-8 text-center text-slate-500 text-sm">No published books yet.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="author-books-grid">
            {books.map(b => (
              <Link key={b.book_id} to={`/book/${b.slug}`} className="fl-card overflow-hidden hover:ring-2 hover:ring-violet-500 transition" data-testid={`author-book-${b.book_id}`}>
                {b.cover_url ? (
                  <img src={b.cover_url} alt="" className="w-full aspect-[3/4] object-cover" />
                ) : (
                  <div className="w-full aspect-[3/4] bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 flex items-center justify-center">
                    <BookOpen className="w-10 h-10 text-white/40" />
                  </div>
                )}
                <div className="p-3">
                  <div className="text-white font-bold text-sm line-clamp-2">{b.title}</div>
                  <div className="text-slate-400 text-xs mt-0.5">{b.chapter_count} chapters · {b.language}</div>
                  {b.for_sale && (b.pdf_price || b.audio_price || b.bundle_price) ? (
                    <div className="text-violet-300 text-[11px] mt-1 fl-mono uppercase tracking-widest">
                      From ${Math.min(...[b.pdf_price, b.audio_price, b.bundle_price].filter(p => p && p > 0))}
                    </div>
                  ) : null}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
